
import React from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useTranslation } from '../i18n';
import { useAuth } from '../contexts/AuthContext';

export const LandingPage = () => {
  const { t } = useTranslation();
  const { user, isGuest } = useAuth();
  const navigate = useNavigate();

  return (
    <div className="min-h-screen bg-gradient-to-b from-blue-50 to-white">
      {/* Hero Section */}
      <section className="relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-r from-blue-600/10 to-purple-600/10"></div>
        <div className="container mx-auto px-4 py-20 relative">
          <div className="text-center max-w-4xl mx-auto">
            <div className="flex justify-center mb-6">
              <img src="/moom1.png" alt="Moomi" className="w-24 h-24 rounded-full shadow-xl" />
            </div>
            <h1 className="text-5xl md:text-6xl font-bold text-gray-800 mb-6">
              {t('landing.title')}
            </h1>
            <p className="text-xl text-gray-600 mb-8 max-w-2xl mx-auto">
              {t('landing.subtitle')}
            </p>
            <div className="flex flex-wrap justify-center gap-4">
              {user && !isGuest ? (
                <Link
                  to="/dashboard"
                  className="bg-blue-600 hover:bg-blue-700 text-white px-8 py-4 rounded-xl text-lg font-semibold transition-all shadow-lg hover:shadow-xl"
                >
                  {t('landing.go_dashboard')}
                </Link>
              ) : isGuest ? (
                <button
                  onClick={() => navigate('/login')}
                  className="bg-blue-600 hover:bg-blue-700 text-white px-8 py-4 rounded-xl text-lg font-semibold transition-all shadow-lg hover:shadow-xl"
                >
                  {t('landing.go_dashboard')}
                </button>
              ) : (
                <Link
                  to="/calculator"
                  className="bg-blue-600 hover:bg-blue-700 text-white px-8 py-4 rounded-xl text-lg font-semibold transition-all shadow-lg hover:shadow-xl"
                >
                  {t('landing.try_calculator')}
                </Link>
              )}
              <a
                href="#learn"
                className="bg-white hover:bg-gray-50 text-gray-700 px-8 py-4 rounded-xl text-lg font-semibold border-2 border-gray-200 transition-all"
              >
                {t('landing.learn_method')}
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Stats */}
      <section className="container mx-auto px-4 py-12">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-6 max-w-4xl mx-auto">
          <div className="text-center bg-white p-4 rounded-xl shadow-md">
            <div className="text-4xl font-bold text-blue-600">✓</div>
            <div className="text-gray-600 text-sm">{t('landing.active_practitioners')}</div>
          </div>
          <div className="text-center bg-white p-4 rounded-xl shadow-md">
            <div className="text-4xl font-bold text-green-600">✓</div>
            <div className="text-gray-600 text-sm">{t('landing.science_backed')}</div>
          </div>
          <div className="text-center bg-white p-4 rounded-xl shadow-md">
            <div className="text-4xl font-bold text-purple-600">✓</div>
            <div className="text-gray-600 text-sm">{t('landing.injury_resistant')}</div>
          </div>
          <div className="text-center bg-white p-4 rounded-xl shadow-md">
            <div className="text-4xl font-bold text-orange-600">⚡</div>
            <div className="text-gray-600 text-sm">{t('landing.time_efficient')}</div>
          </div>
        </div>
      </section>

      {/* Core Principles Section */}
      <section className="container mx-auto px-4 py-16">
        <div className="max-w-4xl mx-auto">
          <h2 className="text-3xl font-bold text-center text-gray-800 mb-8">
            {t('landing.core_principles_title')}
          </h2>
          <div className="grid md:grid-cols-2 gap-6">
            <div className="bg-white p-6 rounded-xl shadow-md hover:shadow-lg transition-all">
              <div className="text-3xl mb-3">🎯</div>
              <h3 className="font-semibold text-gray-800">{t('landing.principle_sub_threshold')}</h3>
              <p className="text-sm text-gray-500 mt-1">{t('landing.principle_sub_threshold_desc')}</p>
            </div>
            <div className="bg-white p-6 rounded-xl shadow-md hover:shadow-lg transition-all">
              <div className="text-3xl mb-3">📊</div>
              <h3 className="font-semibold text-gray-800">{t('landing.principle_control')}</h3>
              <p className="text-sm text-gray-500 mt-1">{t('landing.principle_control_desc')}</p>
            </div>
            <div className="bg-white p-6 rounded-xl shadow-md hover:shadow-lg transition-all">
              <div className="text-3xl mb-3">🔄</div>
              <h3 className="font-semibold text-gray-800">{t('landing.principle_recovery')}</h3>
              <p className="text-sm text-gray-500 mt-1">{t('landing.principle_recovery_desc')}</p>
            </div>
            <div className="bg-white p-6 rounded-xl shadow-md hover:shadow-lg transition-all">
              <div className="text-3xl mb-3">📈</div>
              <h3 className="font-semibold text-gray-800">{t('landing.principle_consistency')}</h3>
              <p className="text-sm text-gray-500 mt-1">{t('landing.principle_consistency_desc')}</p>
            </div>
          </div>
        </div>
      </section>

      {/* CTA — перед основным контентом */}
      <section className="bg-gradient-to-r from-blue-600 to-purple-600 py-16 text-white">
        <div className="container mx-auto px-4 text-center">
          <h2 className="text-3xl font-bold mb-4">
            {t('landing.cta_title')}
          </h2>
          <p className="text-xl mb-8 opacity-90 max-w-2xl mx-auto">
            {t('landing.cta_desc')}
          </p>
          <div className="flex flex-wrap justify-center gap-4">
            <Link
              to="/calculator"
              className="bg-white text-blue-600 hover:bg-gray-100 px-8 py-4 rounded-xl text-lg font-semibold transition-all shadow-lg hover:shadow-xl inline-block"
            >
              {t('landing.try_calculator')}
            </Link>
            {user && !isGuest && (
              <Link
                to="/dashboard"
                className="bg-green-500 hover:bg-green-600 text-white px-8 py-4 rounded-xl text-lg font-semibold transition-all shadow-lg hover:shadow-xl inline-block"
              >
                {t('landing.go_dashboard')}
              </Link>
            )}
          </div>
        </div>
      </section>

      {/* Understanding Sub-Threshold */}
      <section className="bg-gray-50 py-16" id="learn">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-3xl font-bold text-center text-gray-800 mb-8">
              {t('landing.understanding_title')}
            </h2>
            <div className="bg-white rounded-xl shadow-lg p-8">
              <h3 className="text-xl font-bold text-gray-800 mb-4">{t('landing.zone_title')}</h3>
              <p className="text-gray-600 mb-6">
                {t('landing.zone_desc')}
              </p>
              <div className="grid md:grid-cols-3 gap-4">
                <div className="bg-blue-50 p-4 rounded-lg text-center">
                  <div className="text-sm text-gray-500">{t('landing.heart_rate')}</div>
                  <div className="text-xl font-bold text-blue-600">85-90% HRmax</div>
                </div>
                <div className="bg-purple-50 p-4 rounded-lg text-center">
                  <div className="text-sm text-gray-500">{t('landing.blood_lactate')}</div>
                  <div className="text-xl font-bold text-purple-600">2-3 mmol/L</div>
                </div>
                <div className="bg-green-50 p-4 rounded-lg text-center">
                  <div className="text-sm text-gray-500">{t('landing.perceived_effort')}</div>
                  <div className="text-xl font-bold text-green-600">6-7/10 RPE</div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Weekly Structure */}
      <section className="container mx-auto px-4 py-16">
        <div className="max-w-4xl mx-auto">
          <h2 className="text-3xl font-bold text-center text-gray-800 mb-8">
            {t('landing.weekly_title')}
          </h2>
          <p className="text-center text-gray-600 mb-8">
            {t('landing.weekly_subtitle')}
          </p>
          <div className="bg-white rounded-xl shadow-lg overflow-hidden">
            <div className="grid grid-cols-7 gap-px bg-gray-200">
              {['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'].map((day, i) => (
                <div key={i} className="bg-white p-4 text-center min-h-[120px] flex flex-col justify-center">
                  <div className="font-bold text-sm text-gray-500">{day}</div>
                  <div className="mt-2 text-xs">
                    {i === 0 && (
                      <><span className="text-green-600 font-semibold">{t('landing.easy')}</span><br /><span className="text-gray-400">45-60 min</span></>
                    )}
                    {i === 1 && (
                      <><span className="text-red-600 font-semibold">{t('landing.sub_t')}</span><br /><span className="text-gray-400">3×10 min</span><br /><span className="text-gray-400 text-[10px]">90s rec</span></>
                    )}
                    {i === 2 && (
                      <><span className="text-green-600 font-semibold">{t('landing.easy')}</span><br /><span className="text-gray-400">45-60 min</span></>
                    )}
                    {i === 3 && (
                      <><span className="text-red-600 font-semibold">{t('landing.sub_t')}</span><br /><span className="text-gray-400">6×5 min</span><br /><span className="text-gray-400 text-[10px]">60s rec</span></>
                    )}
                    {i === 4 && (
                      <><span className="text-gray-400">{t('landing.rest')}</span><br /><span className="text-gray-400 text-[10px]">or 30 min jog</span></>
                    )}
                    {i === 5 && (
                      <><span className="text-red-600 font-semibold">{t('landing.sub_t')}</span><br /><span className="text-gray-400">10×3 min</span><br /><span className="text-gray-400 text-[10px]">60s rec</span></>
                    )}
                    {i === 6 && (
                      <><span className="text-blue-600 font-semibold">{t('landing.long')}</span><br /><span className="text-gray-400">90-120 min</span></>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Session Options */}
      <section className="bg-gray-50 py-16">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-3xl font-bold text-center text-gray-800 mb-8">
              {t('landing.session_options_title')}
            </h2>
            <div className="grid md:grid-cols-2 gap-6">
              <div className="bg-white p-6 rounded-xl shadow-md">
                <h3 className="font-bold text-lg mb-3 text-blue-600">{t('landing.classic_intervals')}</h3>
                <ul className="space-y-2 text-gray-600">
                  <li>• 8-12 × 3-4min (60s rec)</li>
                  <li>• 4-6 × 6-8min (60s rec)</li>
                  <li>• 3 × 10-12min (90s rec)</li>
                </ul>
              </div>
              <div className="bg-white p-6 rounded-xl shadow-md">
                <h3 className="font-bold text-lg mb-3 text-purple-600">{t('landing.distance_reps')}</h3>
                <ul className="space-y-2 text-gray-600">
                  <li>• 8-12 × 1K (60s rec)</li>
                  <li>• 4-6 × 2K (60s rec)</li>
                  <li>• 3-4 × 3K (90s rec)</li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      </section>

               {/* Distance Applicability */}
        <section className="bg-gray-50 py-16">
          <div className="container mx-auto px-4">
            <div className="max-w-4xl mx-auto">
              <h2 className="text-3xl font-bold text-center text-gray-800 mb-8">
                {t('landing.applicability_title')}
              </h2>
              <div className="space-y-4">
                <div className="bg-white p-6 rounded-xl shadow-md border-l-4 border-green-500">
                  <h3 className="font-bold text-gray-800">{t('landing.applicability_5k_hm')}</h3>
                  <p className="text-gray-600 text-sm">{t('landing.applicability_5k_hm_desc')}</p>
                </div>
                <div className="bg-white p-6 rounded-xl shadow-md border-l-4 border-blue-500">
                  <h3 className="font-bold text-gray-800">{t('landing.applicability_mile')}</h3>
                  <p className="text-gray-600 text-sm">{t('landing.applicability_mile_desc')}</p>
                </div>
                <div className="bg-white p-6 rounded-xl shadow-md border-l-4 border-orange-500">
                  <h3 className="font-bold text-gray-800">{t('landing.applicability_marathon')}</h3>
                  <p className="text-gray-600 text-sm">{t('landing.applicability_marathon_desc')}</p>
                </div>
              </div>
            </div>
          </div>
        </section>

              {/* Individualization */}
        <section className="container mx-auto px-4 py-16">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-3xl font-bold text-center text-gray-800 mb-8">
              {t('landing.individualization_title')}
            </h2>
            <div className="grid md:grid-cols-2 gap-6">
              <div className="bg-white p-6 rounded-xl shadow-md">
                <div className="text-3xl mb-3">🌡️</div>
                <h3 className="font-semibold text-gray-800">{t('landing.individualization_heat')}</h3>
                <p className="text-sm text-gray-500 mt-1">{t('landing.individualization_heat_desc')}</p>
              </div>
              <div className="bg-white p-6 rounded-xl shadow-md">
                <div className="text-3xl mb-3">🔄</div>
                <h3 className="font-semibold text-gray-800">{t('landing.individualization_retest')}</h3>
                <p className="text-sm text-gray-500 mt-1">{t('landing.individualization_retest_desc')}</p>
              </div>
            </div>
          </div>
        </section>

      {/* Getting Started */}
      <section className="container mx-auto px-4 py-16">
        <div className="max-w-4xl mx-auto">
          <h2 className="text-3xl font-bold text-center text-gray-800 mb-8">
            {t('landing.getting_started_title')}
          </h2>
          <div className="grid md:grid-cols-3 gap-6">
            <div className="bg-white p-6 rounded-xl shadow-md text-center">
              <div className="text-4xl font-bold text-blue-600 mb-3">1</div>
              <h3 className="font-bold text-gray-800">{t('landing.step1')}</h3>
              <p className="text-sm text-gray-600 mt-2">{t('landing.step1_desc')}</p>
            </div>
            <div className="bg-white p-6 rounded-xl shadow-md text-center">
              <div className="text-4xl font-bold text-blue-600 mb-3">2</div>
              <h3 className="font-bold text-gray-800">{t('landing.step2')}</h3>
              <p className="text-sm text-gray-600 mt-2">{t('landing.step2_desc')}</p>
            </div>
            <div className="bg-white p-6 rounded-xl shadow-md text-center">
              <div className="text-4xl font-bold text-blue-600 mb-3">3</div>
              <h3 className="font-bold text-gray-800">{t('landing.step3')}</h3>
              <p className="text-sm text-gray-600 mt-2">{t('landing.step3_desc')}</p>
            </div>
          </div>
        </div>
      </section>

      {/* Signs */}
      <section className="bg-green-50 py-16">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-3xl font-bold text-center text-gray-800 mb-8">
              {t('landing.signs_title')}
            </h2>
            <div className="grid md:grid-cols-3 gap-6">
              <div className="bg-white p-6 rounded-xl shadow-md text-center">
                <div className="text-3xl mb-2">🔄</div>
                <h3 className="font-bold text-gray-800">{t('landing.sign1')}</h3>
                <p className="text-sm text-gray-600 mt-1">{t('landing.sign1_desc')}</p>
              </div>
              <div className="bg-white p-6 rounded-xl shadow-md text-center">
                <div className="text-3xl mb-2">🎯</div>
                <h3 className="font-bold text-gray-800">{t('landing.sign2')}</h3>
                <p className="text-sm text-gray-600 mt-1">{t('landing.sign2_desc')}</p>
              </div>
              <div className="bg-white p-6 rounded-xl shadow-md text-center">
                <div className="text-3xl mb-2">📈</div>
                <h3 className="font-bold text-gray-800">{t('landing.sign3')}</h3>
                <p className="text-sm text-gray-600 mt-1">{t('landing.sign3_desc')}</p>
              </div>
            </div>
          </div>
        </div>
      </section>


      {/* Science */}
      <section className="container mx-auto px-4 py-16">
        <div className="max-w-4xl mx-auto">
          <h2 className="text-3xl font-bold text-center text-gray-800 mb-8">
            {t('landing.science_title')}
          </h2>
          <div className="space-y-4">
            <div className="bg-white p-6 rounded-xl shadow-md border-l-4 border-blue-500">
              <h3 className="font-bold text-gray-800">{t('landing.science1')}</h3>
              <p className="text-gray-600 text-sm">{t('landing.science1_desc')}</p>
            </div>
            <div className="bg-white p-6 rounded-xl shadow-md border-l-4 border-purple-500">
              <h3 className="font-bold text-gray-800">{t('landing.science2')}</h3>
              <p className="text-gray-600 text-sm">{t('landing.science2_desc')}</p>
            </div>
            <div className="bg-white p-6 rounded-xl shadow-md border-l-4 border-green-500">
              <h3 className="font-bold text-gray-800">{t('landing.science3')}</h3>
              <p className="text-gray-600 text-sm">{t('landing.science3_desc')}</p>
            </div>
            <div className="bg-white p-6 rounded-xl shadow-md border-l-4 border-orange-500">
              <h3 className="font-bold text-gray-800">{t('landing.science4')}</h3>
              <p className="text-gray-600 text-sm">{t('landing.science4_desc')}</p>
            </div>
          </div>
        </div>
      </section>

      {/* Community */}
      <section className="bg-gray-50 py-16">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-3xl font-bold text-center text-gray-800 mb-8">
              {t('landing.community_title')}
            </h2>
            <div className="grid md:grid-cols-2 gap-6">
              <a
                href="https://www.letsrun.com/forum/flat_read.php?thread=12130781&page=1"
                target="_blank"
                rel="noopener noreferrer"
                className="bg-white p-6 rounded-xl shadow-md hover:shadow-lg transition-all block"
              >
                <h3 className="font-bold text-lg text-gray-800 mb-2">
                  {t('landing.letsrun_title')}
                </h3>
                <p className="text-gray-600 text-sm mb-4">
                  {t('landing.letsrun_desc')}
                </p>
                <div className="flex items-center justify-between">
                  <div className="text-sm text-gray-500">
                    <span className="font-semibold text-gray-700">5,000+ posts</span>
                    <span className="mx-2">·</span>
                    <span>Since 2022</span>
                  </div>
                  <span className="text-blue-600 font-semibold text-sm">
                    {t('landing.read_thread')} →
                  </span>
                </div>
              </a>

              <a
                href="https://www.reddit.com/r/NorwegianSinglesRun/"
                target="_blank"
                rel="noopener noreferrer"
                className="bg-white p-6 rounded-xl shadow-md hover:shadow-lg transition-all block"
              >
                <h3 className="font-bold text-lg text-gray-800 mb-2">
                  {t('landing.reddit_title')}
                </h3>
                <p className="text-gray-600 text-sm mb-4">
                  {t('landing.reddit_desc')}
                </p>
                <div className="flex items-center justify-between">
                  <div className="text-sm text-gray-500">
                    <span className="font-semibold text-gray-700">r/NorwegianSinglesRun</span>
                  </div>
                  <span className="text-orange-600 font-semibold text-sm">
                    {t('landing.visit_subreddit')} →
                  </span>
                </div>
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section className="bg-gradient-to-r from-blue-600 to-purple-600 py-16 text-white">
        <div className="container mx-auto px-4 text-center">
          <h2 className="text-3xl font-bold mb-4">
            {t('landing.cta_title2')}
          </h2>
          <p className="text-xl mb-8 opacity-90 max-w-2xl mx-auto">
            {t('landing.cta_desc2')}
          </p>
          <div className="flex flex-wrap justify-center gap-4">
            <Link
              to="/calculator"
              className="bg-white text-blue-600 hover:bg-gray-100 px-8 py-4 rounded-xl text-lg font-semibold transition-all shadow-lg hover:shadow-xl inline-block"
            >
              {t('landing.try_calculator')}
            </Link>
            {user && !isGuest && (
              <Link
                to="/dashboard"
                className="bg-green-500 hover:bg-green-600 text-white px-8 py-4 rounded-xl text-lg font-semibold transition-all shadow-lg hover:shadow-xl inline-block"
              >
                {t('landing.go_dashboard')}
              </Link>
            )}
          </div>
        </div>
      </section>

        {/* Footer */}
        <footer className="bg-gray-800 text-white py-8 relative">
          <div className="container mx-auto px-4 text-center">
            <p className="text-sm opacity-75">
              {t('landing.footer')}
            </p>
            <p className="text-xs opacity-50 mt-1">
              {t('landing.footer_sub')}
            </p>

            </div>
        </footer>
    </div>
  );
};